// src/features/dzban/pages/DzbanBrands.jsx
// DARK THEME VERSION - WITH THUMBNAILS
// TOP 3 Podium + Thumbnails + Buttons in banner + Collapsible (max 2)
// SEO OPTIMIZED - MAY 2026

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Car, Trophy, ArrowLeft, TrendingUp, Flame, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useDzbanVoting } from '../hooks/useDzbanVoting';

export const DzbanBrands = () => {
  const navigate = useNavigate();
  const { fetchBrandRanking } = useDzbanVoting();
  const [brandRanking, setBrandRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const loadRanking = async () => {
      setLoading(true);
      const data = await fetchBrandRanking();
      setBrandRanking(data);
      setLoading(false);
    };
    
    loadRanking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const top3 = brandRanking.slice(0, 3);
  const rest = brandRanking.slice(3);
  const displayedRest = showAll ? rest : rest.slice(0, 2);

  const getMedalEmoji = (position) => {
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    return `${position}.`;
  };

  const getBarColor = (position) => {
    if (position === 1) return 'from-yellow-400 to-amber-500';
    if (position === 2) return 'from-gray-300 to-gray-400';
    if (position === 3) return 'from-orange-400 to-amber-600';
    return 'from-red-400 to-red-600';
  };

  return (
    <>
      <Helmet>
        {/* SEO OPTIMIZED - TARGET: "bmw parkowanie" + "bmw vs audi vs mercedes" */}
        <title>Ranking Marek Aut - BMW vs Audi vs Mercedes | Kto Najgorzej Parkuje 2026?</title>
        <meta name="description" content={`Która marka samochodu najgorzej parkuje? Sprawdź ranking TOP 20 marek 2026! ${brandRanking[0] ? `${brandRanking[0].name} prowadzi z ${brandRanking[0].totalVotes} głosami na chamstwo drogowe. ` : ''}BMW vs Audi vs Mercedes - społeczny ranking najgorszego parkowania. Mistrz parkowania według marki auta - wyniki głosowania Dzban Roku!`} />
        <meta name="keywords" content="bmw parkowanie, audi parkowanie, mercedes parkowanie, ranking marek aut, bmw vs audi, najgorsi kierowcy bmw, chamstwo drogowe marki, volkswagen parkowanie, ranking parkowanie marki, bmw złe parkowanie, audi chamstwo drogowe, dzban roku marki 2026" />
        
        {/* Dynamic OG based on winner brand */}
        {brandRanking[0] && (
          <>
            <meta property="og:title" content={`🥇 ${brandRanking[0].name} - Najgorsze Parkowanie Polski! ${brandRanking[0].totalVotes} głosów`} />
            <meta property="og:description" content={`${brandRanking[0].name} wygrywa ranking najgorszego parkowania 2026! ${brandRanking[0].spotCount} spotów chamstwa drogowego. BMW vs Audi vs Mercedes - sprawdź pełny ranking marek.`} />
          </>
        )}
        {!brandRanking[0] && (
          <>
            <meta property="og:title" content="Ranking Marek - BMW vs Audi vs Mercedes - Kto Najgorzej Parkuje?" />
            <meta property="og:description" content="Która marka samochodu ma najgorszych kierowców? Ranking chamstwa drogowego 2026!" />
          </>
        )}
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://motoradar24.pl/dzban-roku/marki" />
        <meta property="og:image" content="https://motoradar24.pl/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ranking Marek Aut - BMW vs Audi vs Mercedes 2026" />
        
        {/* Canonical + Robots */}
        <link rel="canonical" href="https://motoradar24.pl/dzban-roku/marki" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
      </Helmet>

      {/* DARK BACKGROUND */}
      <div className="min-h-screen pb-32 lg:pb-4 relative overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Background texture overlay */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.02) 10px, rgba(255,255,255,.02) 20px)',
          }}
        />

        {/* HEADER */}
        <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] text-white py-10 px-4 border-b border-gray-800">
          <div className="max-w-6xl mx-auto relative z-10">
            <button
              onClick={() => navigate('/dzban-roku')}
              className="mb-4 flex items-center gap-2 text-gray-400 hover:text-white transition-all"
              aria-label="Wróć do głosowania Dzban Roku"
            >
              <ArrowLeft size={20} />
              Powrót do głosowania
            </button>

            <div className="text-center">
              {/* H1 with keywords */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <Car 
                  size={40} 
                  className="text-slate-500 animate-pulse"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(100, 116, 139, 0.5))' }}
                  aria-hidden="true"
                />
                <h1 
                  className="text-5xl font-black uppercase tracking-tight"
                  style={{ 
                    fontFamily: '"Bebas Neue", "Impact", sans-serif',
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
                    letterSpacing: '3px'
                  }}
                >
                  RANKING{' '}
                  <span className="bg-gradient-to-r from-slate-500 to-gray-600 bg-clip-text text-transparent">
                    MAREK
                  </span>
                </h1>
                <Car 
                  size={40} 
                  className="text-slate-500 animate-pulse"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(100, 116, 139, 0.5))' }}
                  aria-hidden="true"
                />
              </div>
              
              {/* Subtitle with keywords */}
              <p className="text-lg text-gray-400 italic">
                TOP 20 - BMW vs Audi vs Mercedes - Kto najgorzej parkuje?
              </p>
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div className="max-w-4xl mx-auto p-4 py-8 relative z-10">
          {loading ? (
            <div className="text-center py-20">
              <div 
                className="animate-spin rounded-full h-16 w-16 border-4 border-slate-600 border-t-transparent mx-auto mb-4"
                style={{ boxShadow: '0 0 20px rgba(100, 116, 139, 0.4)' }}
                role="status"
                aria-label="Ładowanie rankingu marek"
              />
              <p className="text-gray-400 font-semibold">Ładowanie rankingu chamstwa drogowego...</p>
            </div>
          ) : brandRanking.length === 0 ? (
            <div 
              className="text-center py-20 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
              style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)' }}
            >
              <Car size={64} className="mx-auto mb-4 text-gray-600" aria-hidden="true" />
              <p className="text-gray-400 font-semibold mb-2 text-xl">
                Ranking marek jest pusty
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Zagłosuj na spoty żeby zobaczyć ranking chamstwa drogowego według marki auta!
              </p>
              <button
                onClick={() => navigate('/dzban-roku')}
                className="px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold rounded-xl hover:from-red-700 hover:to-orange-700 transition-all"
                aria-label="Zacznij głosować na chamstwo drogowe"
              >
                Zacznij głosować
              </button>
            </div>
          ) : (
            <>
              {/* Info banner WITH BUTTONS */}
              <div 
                className="bg-white/5 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/10"
                style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)' }}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <TrendingUp size={20} className="text-slate-500" aria-hidden="true" />
                  <h2 className="font-bold text-white">Jak działa ranking marek?</h2>
                </div>
                <p className="text-sm text-gray-400 text-center mb-4">
                  Sumujemy wszystkie głosy oddane na chamstwo drogowe danej marki auta. 
                  BMW vs Audi vs Mercedes - który kierowca najgorzej parkuje? Im więcej dzbanów, tym wyższa pozycja! 🏆
                </p>

                {/* BUTTONS IN BANNER */}
                <div className="flex gap-2 justify-center flex-wrap">
                  <button
                    onClick={() => navigate('/dzban-roku')}
                    className="px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white text-sm font-bold rounded-lg hover:from-red-700 hover:to-orange-700 transition-all flex items-center gap-2"
                    aria-label="Zagłosuj na chamstwo drogowe"
                  >
                    <Flame size={16} aria-hidden="true" />
                    Zagłosuj
                  </button>
                  
                  <button
                    onClick={() => navigate('/dzban-roku/ranking')}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-sm font-bold rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all flex items-center gap-2"
                    aria-label="Zobacz TOP 5 spotów"
                  >
                    <Trophy size={16} aria-hidden="true" />
                    TOP 5
                  </button>
                  
                  <button
                    onClick={() => navigate('/dzban-roku/miasta')}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center gap-2"
                    aria-label="Zobacz ranking miast"
                  >
                    <MapPin size={16} aria-hidden="true" />
                    Miasta
                  </button>
                </div>
              </div>

              {/* Full ranking - all brands */}
              <div className="space-y-3">
                {brandRanking.slice(0, 3).map((brand, index) => {
                  const position = index + 1;
                  const maxVotes = brandRanking[0].totalVotes;
                  const percentage = (brand.totalVotes / maxVotes) * 100;
                  
                  return (
                    <div 
                      key={brand.name}
                      className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                      role="article"
                      aria-label={`${position} miejsce: ${brand.name} - ${brand.totalVotes} głosów na chamstwo drogowe`}
                    >
                      <div className="p-4">
                        <div className="flex items-center gap-4 mb-3">
                          {/* Position */}
                          <div className="text-3xl font-black text-gray-400 w-12 text-center" aria-label={`Miejsce ${position}`}>
                            {getMedalEmoji(position)}
                          </div>

                          {/* Name & Stats */}
                          <div className="flex-1">
                            <div className="text-xl font-black text-gray-900">
                              {brand.name}
                            </div>
                            <div className="text-sm text-gray-600">
                              {brand.spotCount} {brand.spotCount === 1 ? 'spot' : 'spotów'} złego parkowania
                            </div>
                          </div>

                          {/* Votes */}
                          <div className="text-right">
                            <div className="text-2xl font-black text-red-600">
                              {brand.totalVotes}
                            </div>
                            <div className="text-xs text-gray-500">
                              głosów
                            </div>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div 
                            className={`h-full bg-gradient-to-r ${getBarColor(position)} transition-all duration-500`}
                            style={{ width: `${percentage}%` }}
                            role="progressbar"
                            aria-valuenow={percentage}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>

                        {/* Top 3 spots from this brand */}
                        {brand.topSpots.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-500 mb-2">
                              TOP spoty chamstwa drogowego {brand.name}:
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              {brand.topSpots.map((spot) => (
                                <div 
                                  key={spot.id}
                                  onClick={() => navigate(`/pojazd/${spot.id}`)}
                                  className="cursor-pointer hover:opacity-80 transition-opacity"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Zobacz spot ${brand.name} ${spot.plate} - ${spot.votes} głosów`}
                                >
                                  <img 
                                    src={spot.images?.[0] || spot.imageUrl || 'https://via.placeholder.com/100'}
                                    alt={`Chamstwo drogowe ${brand.name} ${spot.plate} - złe parkowanie`}
                                    className="w-full aspect-square object-cover rounded-lg"
                                  />
                                  <div className="text-xs font-bold text-center mt-1">
                                    {spot.plate} ({spot.votes} 👎)
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* REST - Collapsible (max 2 when collapsed) */}
                {rest.length > 0 && (
                  <>
                    {displayedRest.map((brand, index) => {
                      const position = index + 4;
                      const maxVotes = brandRanking[0].totalVotes;
                      const percentage = (brand.totalVotes / maxVotes) * 100;
                      
                      return (
                        <div 
                          key={brand.name}
                          className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                          role="article"
                          aria-label={`${position} miejsce: ${brand.name} - ${brand.totalVotes} głosów`}
                        >
                          <div className="p-4">
                            <div className="flex items-center gap-4 mb-3">
                              <div className="text-3xl font-black text-gray-400 w-12 text-center">
                                {position}.
                              </div>

                              <div className="flex-1">
                                <div className="text-xl font-black text-gray-900">
                                  {brand.name}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {brand.spotCount} {brand.spotCount === 1 ? 'spot' : 'spotów'}
                                </div>
                              </div>

                              <div className="text-right">
                                <div className="text-2xl font-black text-red-600">
                                  {brand.totalVotes}
                                </div>
                                <div className="text-xs text-gray-500">głosów</div>
                              </div>
                            </div>

                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                              <div 
                                className={`h-full bg-gradient-to-r ${getBarColor(position)} transition-all duration-500`}
                                style={{ width: `${percentage}%` }}
                                role="progressbar"
                                aria-valuenow={percentage}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              />
                            </div>

                            {/* Top 3 spots */}
                            {brand.topSpots.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="text-xs text-gray-500 mb-2">
                                  TOP spoty {brand.name}:
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  {brand.topSpots.map((spot) => (
                                    <div 
                                      key={spot.id}
                                      onClick={() => navigate(`/pojazd/${spot.id}`)}
                                      className="cursor-pointer hover:opacity-80 transition-opacity"
                                      role="button"
                                      tabIndex={0}
                                      aria-label={`Zobacz spot ${brand.name} ${spot.plate}`}
                                    >
                                      <img 
                                        src={spot.images?.[0] || spot.imageUrl || 'https://via.placeholder.com/100'}
                                        alt={`${brand.name} ${spot.plate} parkowanie`}
                                        className="w-full aspect-square object-cover rounded-lg"
                                      />
                                      <div className="text-xs font-bold text-center mt-1">
                                        {spot.plate} ({spot.votes} 👎)
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Expand/Collapse button */}
                    {rest.length > 2 && (
                      <button
                        onClick={() => setShowAll(!showAll)}
                        className="w-full py-4 bg-slate-600 hover:bg-slate-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                        aria-label={showAll ? 'Zwiń listę marek' : `Zobacz wszystkie ${brandRanking.length} marek`}
                        aria-expanded={showAll}
                      >
                        {showAll ? (
                          <>
                            <ChevronUp size={20} aria-hidden="true" />
                            Zwiń listę
                          </>
                        ) : (
                          <>
                            <ChevronDown size={20} aria-hidden="true" />
                            Zobacz wszystkie {brandRanking.length} marek
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};